/**
 * Copyright Highway9 Networks Inc.
 */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { CarrierInfo, EdgePayload, operatorService } from "../../services/operator-service";
import { OperatorQuery, OperatorRadioQuery, bandConfigData } from "../../types/OperatorQuery";
import { CarrierGatewayCore } from "~/types/CarrierGatewayCore";
import { EdgeConnectedDevice } from "~/types/EdgeConnectedDevice";
import { EdgeConnectedRadio } from "~/types/EdgeConnectedRadio";
import { OBJ_STATUS } from "~/constants";
import type { EdgeWithPool } from "~/types/edge";

export const EDGE_ROLE_SECONDARY = "SECONDARY";
export const SERVICE_CARRIER_GATEWAY = "CARRIER_GATEWAY";

export function isSecondaryEdge(edge?: Pick<EdgeWithPool, "runtimeInfo"> | null) {
  return edge?.runtimeInfo?.controlPlaneInfo?.role === EDGE_ROLE_SECONDARY;
}

/** Matches EdgeTable / SyncEdgeWithPool: edges not in any pool have no edgePoolId. */
export function isStandaloneEdge(edge?: { edgePoolId?: string; isEmptyPool?: boolean } | null) {
  return !edge?.edgePoolId && edge?.isEmptyPool !== true;
}

export function hasCarrierGatewayService(edge?: Pick<EdgeWithPool, "services"> | null) {
  return edge?.services?.some((s) => s?.name === SERVICE_CARRIER_GATEWAY) ?? false;
}
import { isPartnerKpiOnlyUser } from "~/helpers/partnerKpiOnly";

export type MacroIdCountMap = {
  [plmn: string]: number[];
};

type initState = {
  open: boolean;
  current: OperatorQuery | null;
  data: OperatorQuery[];
  error?: string | null;
  timers: { [key: string]: number };
  action: { [key: string]: string };
  export: {
    open: boolean;
    id: string | null;
    isRunning: boolean;
  };
  //per Edge data
  edgeOperators: OperatorQuery[];
  connectedCores: CarrierGatewayCore[];

  // Imported operator data for import flow
  importedOperator: OperatorQuery | null;

  // Operator Radio state
  radio: {
    open: boolean;
    current: OperatorRadioQuery | bandConfigData | null;
    data: OperatorRadioQuery[];
    error?: string | null;
  };

  // Carrier Gateway Edge Connected
  edgeConnectedDevices: EdgeConnectedDevice[];
  edgeConnectedRadios: EdgeConnectedRadio[];

  // MacroId count map by PLMN
  macroIdCountMap: MacroIdCountMap;

  carrierInfo: CarrierInfo[];
  edgeContext: {
    isSecondaryEdge: boolean;
    hasCarrierGatewayService: boolean;
    isStandaloneEdge: boolean;
  };
}

export const initialState: initState = {
  open: false,
  current: null,
  edgeOperators: [],
  data: [],
  timers: {},
  action: {},
  export: {
    open: false,
    id: null,
    isRunning: false,
  },
  connectedCores: [],

  // Imported operator initial state
  importedOperator: null,

  // Operator Radio initial state
  radio: {
    open: false,
    current: null,
    data: [],
  },

  // Carrier Gateway  Edge Connected initial state
  edgeConnectedDevices: [],
  edgeConnectedRadios: [],

  // MacroId count map initial state
  macroIdCountMap: {},

  carrierInfo: [],
  edgeContext: {
    isSecondaryEdge: false,
    hasCarrierGatewayService: false,
    isStandaloneEdge: false,
  },
};

export const fetchAllOperators = createAsyncThunk("operator/fetchAllOperators", async () => {
  try {
    const response = await operatorService.getAllOperators();
    return response;
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const fetchOperatorsByEdge = createAsyncThunk("operator/fetchOperatorsByEdge", async (edgeId: string) => {
  const response = await operatorService.getOperatorsByEdge(edgeId);
  return response;
});

export const fetchConnectedCoresByOperator = createAsyncThunk("operator/fetchConnectedCoresByOperator", async (operatorId: string) => {
  const response = await operatorService.getCarrierCore({
    operatorId,
  });
  return response;
});

export const fetchConnectedEdgeDevices = createAsyncThunk("operator/fetchConnectedEdgeDevices", async (payload: EdgePayload) => {
  const response = await operatorService.getConnectedEdgeDevices(payload);
  return response;
});

export const fetchOperatorRadios = createAsyncThunk(
  "operator/fetchOperatorRadios",
  async (operatorIds: string[]) => {
    const requestPayload = {
      operatorIds: operatorIds,
    };
    const response = await operatorService.getOperatorRadios(requestPayload);
    return response;
  }
);

export const fetchCGWEdgeConnectedRadios = createAsyncThunk(
  "operator/fetchCGWEdgeConnectedRadios",
  async (payload: EdgePayload) => {
    try {
      const data = await operatorService.getEdgeConnectedRadios(payload);
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export type FetchCarrierInfoArg = {
  /** When true, run even for RAN_KPI_ONLY users (e.g. dashboard carrier widgets). */
  skipPartnerKpiOnlyGuard?: boolean;
};

export const fetchCarrierInfo = createAsyncThunk<CarrierInfo[], FetchCarrierInfoArg | void>(
  "operator/fetchCarrierInfo",
  async () => {
    try {
      const response = await operatorService.getCarrierInfo();
      return response;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY; carrier info is not surfaced in KPI UI
  // except where explicitly requested (e.g. dashboard).
  {
    condition: (arg) =>
      Boolean((arg as FetchCarrierInfoArg | undefined)?.skipPartnerKpiOnlyGuard) || !isPartnerKpiOnlyUser(),
  },
);

const operatorSlice = createSlice({
  name: "operator",
  initialState,
  reducers: {
    setEdgeContext: (state, action: PayloadAction<{ isSecondaryEdge: boolean; hasCarrierGatewayService: boolean; isStandaloneEdge: boolean }>) => {
      state.edgeContext = action.payload;
    },
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setValues: (state, action: PayloadAction<OperatorQuery | null>) => {
      state.current = action.payload;
    },
    setData: (state, action: PayloadAction<OperatorQuery[]>) => {
      state.data = action.payload;
    },
    setTimer: (state, action: PayloadAction<{ operatorId: string; time: number }>) => {
      state.timers[action.payload.operatorId] = action.payload.time;
    },
    decrementTimer: (state, action: PayloadAction<string>) => {
      if (state.timers[action.payload] > 0) {
        state.timers[action.payload] -= 1;
      }
    },
    setAction: (state, action: PayloadAction<{ operatorId: string; actionType: string }>) => {
      state.action[action.payload.operatorId] = action.payload.actionType;
    },
    setExportOpen: (state, action: PayloadAction<boolean>) => {
      state.export.open = action.payload;
    },
    setExportId: (state, action: PayloadAction<string | null>) => {
      state.export.id = action.payload;
    },
    setExportIsRunning: (state, action: PayloadAction<boolean>) => {
      state.export.isRunning = action.payload
    },
    setImportedOperator: (state, action: PayloadAction<OperatorQuery | null>) => {
      state.importedOperator = action.payload;
    },

    // Operator Radio reducers
    setRadioOpen: (state, action: PayloadAction<boolean>) => {
      state.radio.open = action.payload;
    },
    setRadioSelectedRow: (state, action: PayloadAction<OperatorRadioQuery>) => {
      state.radio.current = action.payload;
    },
    setRadioData: (state, action: PayloadAction<OperatorRadioQuery[]>) => {
      state.radio.data = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchAllOperators.fulfilled, (state, action) => {
        state.data = action.payload;
        // Build macroIdCountMap from operators data
        state.macroIdCountMap = buildMacroIdCountMap(action.payload);
      })
      .addCase(fetchAllOperators.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchOperatorsByEdge.fulfilled, (state, action) => {
        state.edgeOperators = action.payload;
        // For current operator, update the edgeOperators
        if (state.current && !state.open) {
          const currentOperator = action.payload.find((operator) => operator.id === state.current?.id);
          if (currentOperator) {
            state.current = currentOperator;
          }
        }
      })
      .addCase(fetchOperatorsByEdge.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchConnectedCoresByOperator.fulfilled, (state, action) => {
        state.connectedCores = action.payload.data;
      })
      .addCase(fetchConnectedCoresByOperator.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchOperatorRadios.fulfilled, (state, action) => {
        state.radio.data = action.payload;
      })
      .addCase(fetchOperatorRadios.rejected, (state, action) => {
        state.radio.error = action.error.message;
      })
      .addCase(fetchConnectedEdgeDevices.fulfilled, (state, action) => {
        state.edgeConnectedDevices = action.payload.data;
      })
      .addCase(fetchConnectedEdgeDevices.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchCGWEdgeConnectedRadios.fulfilled, (state, action) => {
        state.edgeConnectedRadios = action.payload.data;
      })
      .addCase(fetchCGWEdgeConnectedRadios.rejected, (state, action) => {
        console.error(action.error);
        state.edgeConnectedRadios = [];
      })
      .addCase(fetchCarrierInfo.fulfilled, (state, action) => {
        state.carrierInfo = action.payload;
      })
      .addCase(fetchCarrierInfo.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

export const operatorActions = operatorSlice.actions;
export default operatorSlice.reducer;

export const operatorOpen = (state: RootState) => state.operator.open;
export const operatorState = (state: RootState) => state.operator.current;
export const operatorEdgeOperators = (state: RootState) => state.operator.edgeOperators;
export const operatorConnectedCores = (state: RootState) => state.operator.connectedCores;
export const operatorData = (state: RootState) => state.operator.data;
export const operatorTimer = (state: RootState) => state.operator.timers;
export const operatorAction = (state: RootState) => state.operator.action;
export const operatorExportOpen = (state: RootState) => state.operator.export.open;
export const operatorExportId = (state: RootState) => state.operator.export.id;
export const operatorExportIsRunning = (state: RootState) => state.operator.export.isRunning;
export const operatorImportedData = (state: RootState) => state.operator.importedOperator;

// Operator Radio selectors
export const operatorRadioOpen = (state: RootState) => state.operator.radio.open;
export const operatorRadioState = (state: RootState) => state.operator.radio.current;
export const operatorRadiosData = (state: RootState) => state.operator.radio.data;

// Carrier Gateway Edge Connected selectors
export const operatorEdgeConnectedDevices = (state: RootState) => state.operator.edgeConnectedDevices;
export const operatorEdgeConnectedRadios = (state: RootState) => state.operator.edgeConnectedRadios;

// MacroId count map selector
export const operatorMacroIdCountMap = (state: RootState) => state.operator.macroIdCountMap;
export const operatorEdgeContext = (state: RootState) => state.operator.edgeContext;
export const operatorEdgeEditLocked = (state: RootState) =>
  state.operator.edgeContext.isSecondaryEdge
  && state.operator.edgeContext.hasCarrierGatewayService
  && !state.operator.edgeContext.isStandaloneEdge;



/**
 * Builds a map of macroIds organized by PLMN, tracking which macroIds are in use.
 * Used to disable macroIds that are already assigned to other operators within the same PLMN.
 */
function buildMacroIdCountMap(operators: OperatorQuery[]): MacroIdCountMap {
  const plmnMacroIdSets: Record<string, Set<number>> = {};

  for (const operator of operators) {
    // If the operator is disabled, its macroIds are considered available for others to use
    if (operator.status === OBJ_STATUS.DISABLED) {
      continue;
    }

    const plmnList = operator.coreConfig.plmn ?? [];
    const macroIdList = operator.coreConfig.macroRadioConfig?.macroDetails?.macroIdList ?? [];

    for (const plmn of plmnList) {
      const set = plmnMacroIdSets[plmn] ??= new Set();
      macroIdList.forEach(id => set.add(id));
    }
  }
  // Convert Sets to arrays
  return Object.fromEntries(
    Object.entries(plmnMacroIdSets).map(([plmn, ids]) => [plmn, [...ids]])
  );
}
export const operatorCarrierInfo = (state: RootState) => state.operator.carrierInfo;
